import { useState } from "react";
import Sidebar from "../../components/feature/Sidebar";
import TopBar from "../../components/feature/TopBar";
import BankAccount from "./components/BankAccount";
import BudgetTable from "./components/BudgetTable";
import TaxEventPanel from "./components/TaxEventPanel";
import JournalEntry from "./components/JournalEntry";
import FoundationFinance from "./components/FoundationFinance";
import { useCompany } from "../../contexts/CompanyContext";
import { useRole } from "../../contexts/RoleContext";

type Tab = "bank" | "budget" | "tax" | "journal";

export default function FinancePage() {
  const [activeTab, setActiveTab] = useState<Tab>("bank");
  const { companyState } = useCompany();
  const { companyType, companyConfig } = useRole();
  const isFoundation = companyType === "CulturalFoundation";

  const tabs: { id: Tab; label: string; icon: string; badge?: number }[] = isFoundation
    ? [
        { id: "bank", label: "가상 계좌", icon: "ri-bank-card-line" },
        { id: "journal", label: "전표처리", icon: "ri-file-edit-line" },
        { id: "tax", label: "세금 & 청구서", icon: "ri-government-line", badge: 2 },
      ]
    : [
        { id: "bank", label: "가상 은행 계좌", icon: "ri-bank-card-line" },
        { id: "budget", label: "예산 관리표", icon: "ri-bar-chart-grouped-line" },
        { id: "tax", label: "세금 & 청구서", icon: "ri-government-line", badge: 2 },
        { id: "journal", label: "전표처리", icon: "ri-file-edit-line" },
      ];

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex font-sans">
      <Sidebar />
      <div className="flex-1 ml-56 flex flex-col">
        <TopBar />
        <main className="flex-1 p-5">
          {/* Header */}
          <div className="bg-[#111112] border border-white/[0.06] rounded-xl px-5 py-4 flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#4B6CF7]/10 border border-[#4B6CF7]/20">
                <i className="ri-bank-line text-[#7A9BFF] text-lg"></i>
              </div>
              <div>
                <h1 className="text-[#F2F2F2] font-semibold text-sm">
                  {isFoundation ? "비영리 회계 시스템" : "재무 / 회계 ERP"}
                </h1>
                <p className="text-[#444444] text-xs">
                  {isFoundation
                    ? "보조금 수지 · 사업비 집행 · 전표처리"
                    : "가상 자본금 관리 · 예산 집행 · 세무 처리 · 전표처리"}
                </p>
              </div>
              {isFoundation && (
                <span className="text-[9px] px-2 py-0.5 rounded-full border text-[#7A9BFF] border-[#4B6CF7]/30 bg-[#4B6CF7]/10">비영리</span>
              )}
            </div>
            <div className="flex items-center gap-5">
              <div className="text-right">
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest">
                  {isFoundation ? "보조금 잔액" : "현재 잔고"}
                </div>
                <div className="text-[#F2F2F2] font-semibold text-base tabular-nums">
                  {(companyState?.capital ?? 0).toLocaleString()}만원
                </div>
              </div>
              <div className="text-right">
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest">신용도</div>
                <div className="text-[#888888] font-semibold text-base">{companyState?.credit_score ?? 80}pt</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-0.5 mb-5 bg-white/[0.03] rounded-xl p-1 w-fit border border-white/[0.05]">
            {tabs.map(tab => (
              <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id ? "bg-[#4B6CF7]/15 text-[#7A9BFF] border border-[#4B6CF7]/20" : "text-[#444444] hover:text-[#888888]"
                }`}>
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
                {tab.badge && <span className="bg-[#4B6CF7]/20 text-[#7A9BFF] text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full">{tab.badge}</span>}
              </button>
            ))}
          </div>

          {/* Foundation: 수지계산서를 bank 탭에 표시 */}
          {activeTab === "bank" && isFoundation && <FoundationFinance />}
          {activeTab === "bank" && !isFoundation && <BankAccount />}
          {activeTab === "budget" && <BudgetTable />}
          {activeTab === "tax" && <TaxEventPanel />}
          {activeTab === "journal" && <JournalEntry />}
        </main>
      </div>
    </div>
  );
}
